import { Link, useLocation } from 'react-router-dom';

import { useLanguage } from '@/components/LanguageContext';
import { IDIOMAS, paginaDeLaRuta, urlDe } from '@/lib/rutasPublicas';

/**
 * Cada idioma en su propia lengua, sin traducir: quien busca su idioma en una
 * página que no entiende lo reconoce por cómo se escribe en el suyo.
 */
const NOMBRE_IDIOMA = {
  es: 'Español',
  pt: 'Português',
  en: 'English',
  de: 'Deutsch',
  fr: 'Français',
};

/**
 * LandingFooterV3 — pie compartido por las once páginas públicas del V3.
 *
 * En el diseño solo la home lleva pie, y sus seis enlaces legales apuntan todos
 * a `href="#"`. Aquí se montan en todas las páginas y se cablean a las rutas
 * SPA que ya existen. Un enlace legal muerto en una página pública no es un
 * detalle de maquetación: es lo que un usuario busca cuando quiere ejercer un
 * derecho.
 *
 * Los datos de empresa son literales y NO se traducen, igual que en
 * `LandingFooter.jsx`: una razón social y una dirección postal son lo que son.
 */
const LOGO_SRC = '/landing-images/logo.png';

/**
 * Las entradas con `pagina` NO son enlaces fijos: apuntan a la URL de esa
 * página en el idioma que se esté sirviendo (`/pricing`, `/de/preise`…), sacada
 * de `rutasPublicas.js`. Las de `to` son las legales, que tienen una sola URL.
 *
 * Precios apunta a su página propia y no al ancla `#precios` de la home desde
 * que la página vuelve a enlazarse desde la landing (menú y pie).
 */
const LEGAL_LINKS = [
  { pagina: 'precios', key: 'landing.v3.footer.pricing' },
  { pagina: 'sobreNosotros', key: 'landing.v3.footer.about' },
  { to: '/privacy', key: 'landing.v3.footer.privacy' },
  { to: '/dpa', key: 'landing.v3.footer.dpa' },
  { to: '/terms', key: 'landing.v3.footer.terms' },
  { to: '/legal-notice', key: 'landing.v3.footer.legal' },
  { to: '/cookies', key: 'landing.v3.footer.cookies' },
];

/*
 * Las redes. Las URLs son las mismas que ya sirve `LandingFooter.jsx` (el
 * diseño las dejó en `href="#"`), y desde ahora los TRAZOS también.
 *
 * La V3 los traía redibujados a línea para que pegaran con el resto de iconos
 * de estas páginas. Se cambian a los glifos macizos por tres motivos:
 *
 *  1. **Son las letras correctas.** Instagram, LinkedIn y TikTok piden que se
 *     use su marca sin redibujarla ni contornearla; un «in» a trazos no es su
 *     logo, es una interpretación nuestra.
 *  2. **Se leen.** El icono se pinta a 17 px; con `stroke-width: 1.8` sobre un
 *     lienzo de 24 eso es un filete de ~1,3 px, y un «in» contorneado y sin su
 *     cuadrado a ese tamaño se adivina más que se lee.
 *  3. **Los dos pies decían cosas distintas**, y el bueno era el viejo. Como la
 *     V2 se borra entera en el PR 7, si no se rescatan ahora estos glifos se
 *     van con ella.
 *
 * Lucide NO es la alternativa: sus iconos de marca están marcados `@deprecated`
 * y se van en la v1.0, su `Linkedin` es otro redibujo de línea igual que el que
 * había, y no tiene TikTok.
 */
const SOCIAL = [
  {
    label: 'Instagram',
    href: 'https://www.instagram.com/tapstar.es',
    path: 'M12 2.2c3.2 0 3.6 0 4.9.07 1.2.05 1.8.25 2.2.42.6.2 1 .47 1.4.9.43.4.7.8.9 1.4.17.4.37 1 .42 2.2.06 1.3.07 1.7.07 4.9s0 3.6-.07 4.9c-.05 1.2-.25 1.8-.42 2.2-.2.6-.47 1-.9 1.4-.4.43-.8.7-1.4.9-.4.17-1 .37-2.2.42-1.3.06-1.7.07-4.9.07s-3.6 0-4.9-.07c-1.2-.05-1.8-.25-2.2-.42-.6-.2-1-.47-1.4-.9-.43-.4-.7-.8-.9-1.4-.17-.4-.37-1-.42-2.2C2.2 15.6 2.2 15.2 2.2 12s0-3.6.07-4.9c.05-1.2.25-1.8.42-2.2.2-.6.47-1 .9-1.4.4-.43.8-.7 1.4-.9.4-.17 1-.37 2.2-.42C8.4 2.2 8.8 2.2 12 2.2zm0 1.8c-3.1 0-3.5 0-4.7.07-.9.04-1.4.2-1.7.32-.43.17-.74.37-1.06.7-.32.32-.52.63-.7 1.06-.12.3-.28.8-.32 1.7C3.5 9.05 3.5 9.4 3.5 12s0 2.95.07 4.15c.04.9.2 1.4.32 1.7.17.43.37.74.7 1.06.32.32.63.52 1.06.7.3.12.8.28 1.7.32 1.2.06 1.6.07 4.7.07s3.5 0 4.7-.07c.9-.04 1.4-.2 1.7-.32.43-.17.74-.37 1.06-.7.32-.32.52-.63.7-1.06.12-.3.28-.8.32-1.7.06-1.2.07-1.55.07-4.15s0-2.95-.07-4.15c-.04-.9-.2-1.4-.32-1.7-.17-.43-.37-.74-.7-1.06-.32-.32-.63-.52-1.06-.7-.3-.12-.8-.28-1.7-.32C15.5 4 15.1 4 12 4zm0 3.06A4.94 4.94 0 1 1 12 17a4.94 4.94 0 0 1 0-9.88zm0 1.8a3.14 3.14 0 1 0 0 6.28 3.14 3.14 0 0 0 0-6.28zm5.15-2a1.15 1.15 0 1 1 0 2.3 1.15 1.15 0 0 1 0-2.3z',
  },
  {
    label: 'LinkedIn',
    href: 'https://www.linkedin.com/company/tapstar-es',
    path: 'M4.98 3.5a2 2 0 1 1 0 4 2 2 0 0 1 0-4zM3.4 9h3.16v11.5H3.4V9zm5.13 0h3.03v1.57h.04c.42-.8 1.45-1.65 2.99-1.65 3.2 0 3.79 2.1 3.79 4.84v6.74h-3.16v-5.98c0-1.43-.03-3.26-1.99-3.26-1.99 0-2.29 1.55-2.29 3.15v6.09H8.53V9z',
  },
  {
    label: 'TikTok',
    href: 'https://www.tiktok.com/@tapstar.es',
    path: 'M16.5 3c.3 2.1 1.5 3.6 3.5 3.9V9.3c-1.3.1-2.5-.3-3.5-1v6.2c0 3.2-2.3 5.5-5.3 5.5-2.9 0-5.2-2.2-5.2-5.1 0-3 2.4-5.2 5.6-4.9v2.6c-.4-.1-.9-.2-1.3-.1-1.2.1-2.1 1-2 2.4 0 1.3 1 2.3 2.3 2.3 1.4 0 2.3-1 2.3-2.6V3h3.6z',
  },
];

export default function LandingFooterV3() {
  // `language` para que precios y «sobre nosotros» apunten a la página del
  // idioma que se está sirviendo, no siempre a la española.
  const { t, language, setLanguage } = useLanguage();
  // La misma página en cada idioma; si esta no está en la tabla, la home.
  const pagina = paginaDeLaRuta(useLocation().pathname) ?? 'home';

  return (
    <footer className="bg-white px-6 pt-6 pb-8">
      <div className="max-w-[1180px] mx-auto bg-[#f6f5f3] rounded-[28px] px-6 py-10 sm:px-10 grid gap-7 md:grid-cols-2 md:gap-10 items-start">
        <div>
          <img src={LOGO_SRC} alt="Tapstar" className="h-[38px] w-auto block" />
          <p className="text-[14.5px] leading-[1.65] text-v3-body mt-[18px]">
            © 2026 MyTapstar · Speed Data Technologies, S.L.
          </p>
          <p className="text-[14.5px] leading-[1.65] text-v3-body">
            Avenida Primado Reig 129, Entresuelo D, 46020 Valencia · Tel.{' '}
            <a href="tel:+34604934869" className="hover:text-v3-ink transition-colors">
              604 934 869
            </a>
          </p>
          <p className="text-[14.5px] leading-[1.65] text-v3-body">
            Sede: Lanzadera, Sc Puerto, 6, Poblats Marítims, 46024 València, Valencia
          </p>
        </div>

        <div className="flex flex-col items-start md:items-end gap-6">
          <div className="flex gap-3">
            {SOCIAL.map((s) => (
              <a
                key={s.label}
                href={s.href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={s.label}
                className="w-[38px] h-[38px] rounded-full border border-v3-line flex items-center justify-center text-v3-body hover:text-v3-ink hover:border-v3-faint transition-colors"
              >
                {/* Macizo (`fill`), no contorneado: ver la nota de `SOCIAL`.
                    El color lo hereda del enlace, que es lo que le da el
                    `hover` junto con el borde. */}
                <svg
                  width="16"
                  height="16"
                  viewBox="0 0 24 24"
                  fill="currentColor"
                  aria-hidden="true"
                >
                  <path d={s.path} />
                </svg>
              </a>
            ))}
          </div>

          {/* Dos columnas en el diseño. En móvil se dejan fluir con `flex-wrap`
              en vez de forzar la rejilla: seis etiquetas largas en dos columnas
              de 160 px se parten por la mitad. */}
          <div className="flex flex-wrap md:grid md:grid-cols-2 gap-x-10 gap-y-3 md:justify-end md:justify-items-end">
            {/* Fuera las páginas que no existen en este entorno (`urlDe` da
                `null`): hoy «Sobre nosotros» en producción, ver
                `lib/sobreNosotrosGate.js`. Sin esto el pie pintaría un enlace
                a ninguna parte. */}
            {LEGAL_LINKS.filter((l) => !l.pagina || urlDe(l.pagina, language)).map((l) => (
              <Link
                key={l.key}
                to={l.pagina ? urlDe(l.pagina, language) : l.to}
                className="text-[14.5px] text-v3-body hover:text-v3-ink whitespace-nowrap transition-colors"
              >
                {t(l.key)}
              </Link>
            ))}
          </div>

          {/* Conmutador de idioma. Hace falta desde que a los visitantes de
              Portugal se les lleva solos a /pt/ (`lib/idiomaSesion.js`): sin
              él, un hispanohablante en Portugal no tendría cómo volver.
              Pasa por `setLanguage` y no es un enlace pelado porque eso apunta
              la elección en la sesión, y es lo que impide que la home le
              devuelva a /pt/ en cuanto llegue. */}
          <nav aria-label="Idioma" className="flex flex-wrap gap-x-4 gap-y-2 md:justify-end">
            {IDIOMAS.filter((idioma) => urlDe(pagina, idioma)).map((idioma) => (
              <Link
                key={idioma}
                to={urlDe(pagina, idioma)}
                lang={idioma}
                hrefLang={idioma}
                aria-current={idioma === language ? 'true' : undefined}
                onClick={() => setLanguage(idioma)}
                className={`text-[13.5px] whitespace-nowrap transition-colors ${
                  idioma === language ? 'text-v3-ink font-semibold' : 'text-v3-body hover:text-v3-ink'
                }`}
              >
                {NOMBRE_IDIOMA[idioma] ?? idioma}
              </Link>
            ))}
          </nav>
        </div>
      </div>
    </footer>
  );
}
